import { AccredibleKeyboardFocusService } from '@accredible-frontend-v2/services/keyboard-focus';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  Output,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';

const TYPE_DEFAULT_VALUE = 'text';
const AUTOCOMPLETE_DEFAULT_VALUE = 'off';

let nextUniqueId = 0;

type IconPosition = 'start' | 'end';

@Component({
  selector: 'accredible-text-input',
  templateUrl: './text-input.component.html',
  styleUrls: ['./text-input.component.scss'],
  // We are not using ChangeDetectionStrategy.OnPush because this input component
  // does not detect changes when formGroup.markAllAsTouched() is triggered
})
export class AccredibleTextInputComponent implements AfterViewInit, OnDestroy {
  private readonly _keyboardFocus = inject(AccredibleKeyboardFocusService);

  requirementLabel = input<'required' | 'optional'>();
  formControlRef = input<UntypedFormControl>();
  label = input<string>();
  hint = input<string>();
  tooltip = input<string>();
  id = input<string>(`input-text-${nextUniqueId++}`);
  placeholder = input<string>('');
  required = input<boolean>(false);
  type = input<'text' | 'number' | 'email' | 'password'>(TYPE_DEFAULT_VALUE);
  size = input<'small' | 'normal' | 'large'>('normal');
  maxLength = input<number>();
  autoComplete = input<string>(AUTOCOMPLETE_DEFAULT_VALUE);
  readonly = input<boolean>(false);
  icon = input<string>();
  iconPosition = input<IconPosition>('end');
  noError = input<boolean>(false);
  showReset = input<boolean>(false);
  min = input<number>();
  max = input<number>();
  dataCy = input<string>();

  blurEvent = output<Event>();

  // cannot be converted to an output signal or we will lose the 'observed' property which is used for 'isClickable' getter.
  @Output()
  iconClick = new EventEmitter<boolean>();

  // TODO: cannot switch to viewChild signal due to this ng-mocks bug: https://github.com/help-me-mom/ng-mocks/issues/8634
  @ViewChild('textInput')
  private readonly _textInputEl: ElementRef<HTMLElement>;

  isPasswordVisible = signal(false);

  inputType = computed(() => {
    return this.type() === 'password' && this.isPasswordVisible() ? 'text' : this.type();
  });

  isFormControlRequired = computed(() => {
    return this.formControlRef()?.hasValidator(Validators.required) ?? false;
  });

  get isClickable(): boolean {
    return this.iconClick.observed;
  }

  onBlur(event: Event): void {
    this.blurEvent.emit(event);
  }

  ngAfterViewInit(): void {
    if (this._textInputEl) {
      this._keyboardFocus.startFocusMonitoring(this._textInputEl);
    }
  }

  ngOnDestroy(): void {
    if (this._textInputEl) {
      this._keyboardFocus.stopFocusMonitoring(this._textInputEl);
    }
  }

  togglePasswordVisibility(event: MouseEvent): void {
    event.preventDefault();
    this.isPasswordVisible.set(!this.isPasswordVisible());
  }
}
